import type { ComponentProps } from "react";
import { Carousel as UICarousel } from "ancestry-ui-react";
import { useUIComponentLoaded } from "@/shared/hooks/useUIComponentLoaded";

/**
 * Prop types mirror the real `ancestry-ui-react` Carousel by deriving them directly
 * from it, so the wrapper stays a drop-in replacement and cannot drift from
 * `desc.d.ts`.
 */
export type CarouselProps = ComponentProps<typeof UICarousel>;

/**
 * Safe boundary wrapper for `ancestry-ui-react`'s `Carousel` (ADR-012 §3,
 * gate-the-mount).
 *
 * The underlying `Carousel` reads `window.ui.carousel` unconditionally in
 * `componentDidMount`, so mounting it before Ancestry's vanilla UI library
 * (`core.js`) has populated `window.ui` throws and surfaces as a `Main` FCI. We gate
 * the mount on the shared, hydration-safe readiness signal: render nothing until
 * `window.ui.carousel` exists, then mount the real component.
 *
 * A carousel is interaction-gated and paints no visible DOM until it initializes, so
 * gating introduces no layout shift at the boundary.
 */
const SafeCarousel = (props: CarouselProps) => {
    const isLoaded = useUIComponentLoaded("carousel");

    if (!isLoaded) return null;

    return <UICarousel {...props} />;
};

/**
 * Re-expose the real compound `Carousel.Slide` on the wrapper so existing
 * `<Carousel.Slide>` JSX keeps working unchanged, and slides render the exact real
 * component. Slides only ever render as children of the gated `Carousel`, so they
 * never mount before the underlying component does.
 *
 * The member is attached via a lazy getter rather than read eagerly at module load:
 * importing this boundary (barrel) must never touch `ancestry-ui-react` before render
 * — the same deferral the `Callout`/`Modal` wrappers get for free by only referencing
 * the library inside their render body. This keeps the boundary importable under
 * partial test mocks.
 */
export const Carousel = SafeCarousel as typeof SafeCarousel & {
    readonly Slide: (typeof UICarousel)["Slide"];
};

Object.defineProperty(Carousel, "Slide", {
    get: () => UICarousel.Slide,
    enumerable: true,
});
